import * as angular from 'angular';
import { zip } from 'lodash';
import { createSelector, createStructuredSelector, Selector } from 'reselect';

import { Flamingo } from '@mode/charts/util-vegas';
import { EncodingHelpers, SwitchRenderModel, SwitchSchemaModel } from '@mode/charts/util-vegas';

import { ReportViewerState, VizTypes } from '@mode/reports/sandbox/contract';
import {
  EmbedChartState,
  NotebookEmbedVizResult,
  NotebookRunState,
  NotebookVizRender,
  NotebookVizState,
} from '@mode/legacy/types-app-state';
import { FEATURE_FLAG_MAP, Features } from '@mode/shared/util-feature-flags';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { makePagingResult } from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { colorScope } from '../../../lib/chart-coloring-helpers';
import { getFriendlyFlamingoError } from '../../../lib/chart-helpers';
import { ReportFilterControlHelpers } from '@mode/filters/util';
import { ChartInterpolaterService } from '../../chart/domain/chart-interpolater-service';
import { ChartSanitizerService } from '../../chart/domain/chart-sanitizer-service';
import { LayoutSelectors } from './layout/embed-layout.selectors';

/**
 * This is for selecting state from the serialized report state generted in the
 * embed-state.selectors file. Additionally, it can select iframe-only state stored
 * locally, which you can see in embed-report/reducers/index.ts.
 *
 * Since this file is used exclusively in an iframe (or headless viewer) context, it
 * cannot pull state from anything not available in embed-report/reducers/index.ts.
 */

const sanitizer = ChartSanitizerService(console);
const interpolater = ChartInterpolaterService();

export namespace EmbedChartSelectors {
  export function createV1ViewSelector(token: string) {
    return (state: ReportViewerState) => state.reportFeature.serializedReport.charts.v1Views[token];
  }

  export function createQueryTokenSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.queryTokens[token],
      (queryToken) => queryToken
    );
  }

  export function createV2ChartTypeSelector(token: string) {
    return (state: ReportViewerState) => state.reportFeature.serializedReport.charts.chartTypes[token];
  }

  export function createV1ChartTypeSelector(token: string) {
    return createSelector(createV1ViewSelector(token), (v1View) => v1View?.selectedChart);
  }

  export function createQueryFailedSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.queryTokens[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.queryRunStatus,
      (queryToken, queryStatus) =>
        queryStatus[queryToken] != null ? queryStatus[queryToken].state === 'failed' : undefined
    );
  }

  export function createDatasetResultSelector(queryToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.datasetResults,
      (datasetResults) => datasetResults.find((item) => item.queryToken === queryToken)
    );
  }

  /**
   * Note: This will return null if the dataset is null.
   */
  export function createSanitizedV1ViewSelector(token: string, isBuilder: boolean) {
    return createSelector(
      createV1ViewSelector(token),
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.queryTokens[token],
      (state: ReportViewerState) =>
        state.reportFeature.serializedReport.legacyEmbedDatasets[
          state.reportFeature.serializedReport.charts.queryTokens[token]
        ],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.runLinks[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.vizLinks[token],
      (v1View, queryToken, dataset, _vizReportRunLink, _vizLink) => {
        if (v1View != null && queryToken != null && dataset != null) {
          const sanitizedView = angular.copy(v1View);
          sanitizer.sanitize(sanitizedView, dataset, isBuilder);

          // sanitize the view, no interpolation
          return {
            ...sanitizedView,
            _dataset: queryToken,
            _vizReportRunLink,
            _vizLink,
          };
        }

        return null;
      }
    );
  }

  export function createChartSelector(token: string, isBuilder: boolean) {
    return createStructuredSelector({
      chartType: createV2ChartTypeSelector(token),
      colorPaletteToken: (state: ReportViewerState) =>
        state.reportFeature.serializedReport.charts.colorPaletteTokens[token],
      encoding: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.encodings[token],
      format: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.formats[token],
      fieldFormats: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.fieldFormats[token],
      fieldColors: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.fieldColors[token],
      facetSizes: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.facetSizes[token],
      history: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.histories[token],
      queryToken: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.queryTokens[token],
      title: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.titles[token],
      description: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.descriptions[token],
      caption: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.captions[token],
      isAutoUpdateDisabled: (state: ReportViewerState) =>
        state.reportFeature.serializedReport.charts.isAutoUpdateDisabled[token] ?? false,
      v1View: createSanitizedV1ViewSelector(token, isBuilder),
      version: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.versions[token],
      _vizReportRunLink: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.runLinks[token],
      _vizLink: (state: ReportViewerState) => state.reportFeature.serializedReport.charts.vizLinks[token],
      token: () => token,
    });
  }

  export function createVegasRenderInfoSelector(token: string, isBuilder: boolean) {
    return createSelector(
      createChartSelector(token, isBuilder),
      (state: ReportViewerState) => state.reportFeature.serializedReport.reportLevelColorMaps,
      (chart, colorMaps): Readonly<ChartTypes.VegasRenderInfo> =>
        chart.version && {
          title: chart.title,
          description: chart.description,
          caption: chart.caption,
          chartType: chart.chartType,
          encoding: chart.encoding,
          format: chart.format,
          fieldFormats: chart.fieldFormats,
          fieldColors: chart.fieldColors,
          facetSizes: chart.facetSizes,
          isAutoUpdateDisabled: chart.isAutoUpdateDisabled ?? false,
          reportLevelColorMap:
            colorMaps && colorMaps[colorScope(chart.encoding, chart.colorPaletteToken, chart.queryToken)],
          _vizReportRunLink: chart._vizReportRunLink,
          _vizLink: chart._vizLink,
        }
    );
  }

  export function createIsInViewportSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.inViewport,
      (viewport) => viewport[token]
    );
  }

  export function createDownloadsSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.downloadStatus,
      (downloadStatus) => downloadStatus[token]
    );
  }

  export function createIsOrphanChartSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.tokens,
      (tokens) => tokens.every((t) => t !== token)
    );
  }

  export function createEmbedStateSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.histories[token],
      (history): EmbedChartState => ({
        canUndo: history != null && history.undoStack.length > 0,
        canRedo: history != null && history.redoStack.length > 0,
      })
    );
  }

  export function createVizTitleSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.v1Views[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.titles[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.versions[token],
      (v1View, title, version) => {
        // Get vizualization title regardless of version
        if (version === 1 && v1View != null) {
          return v1View ? v1View.chartTitle : '';
        } else {
          return title;
        }
      }
    );
  }

  export function createVizCaptionSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.captions[token],
      (caption) => caption
    );
  }

  export function createEncodedChartTypeSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.v1Views[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.chartTypes[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.versions[token],
      (v1View, type, version) => {
        // Get chart type regardless of version
        if (version === 1 && v1View != null) {
          return v1View.selectedChart ? v1View.selectedChart : 'undefined';
        } else {
          return type;
        }
      }
    );
  }

  export function createRenderDataModelSelector(chartToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.pivotsByViz[chartToken],
      createQueryTokenSelector(chartToken),
      (state: ReportViewerState) =>
        state.reportFeature.serializedReport.flamingo.views[createQueryTokenSelector(chartToken)(state)],
      (pivot, queryToken, view) => {
        if (pivot != null && view != null) {
          return SwitchRenderModel.make(
            Switch.Models.makeFromView(queryToken, view),
            queryToken,
            pivot.plan,
            pivot.result
          );
        }

        return null;
      }
    );
  }

  export function createHierarchySelector(chartToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.encodings[chartToken],
      (state: ReportViewerState) =>
        state.reportFeature.serializedReport.flamingo.views[createQueryTokenSelector(chartToken)(state)],
      (encoding, view) => {
        if (view != null && encoding != null) {
          const model = Switch.Models.makeFromView(view.name, view);
          return EncodingHelpers.canChangeHierarchy(encoding, model, view.name);
        }

        return {
          back: false,
          forward: false,
        };
      }
    );
  }

  export function createFlamingoStateSelector(chartToken: string) {
    return (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.statesByViz[chartToken];
  }

  export function createBigValueMaxFontSizeSelector(chartToken: string) {
    return createSelector(LayoutSelectors.createRowHeightSelector(VizTypes.Chart, chartToken), (rowHeight) => {
      switch (rowHeight) {
        case 'medium':
        case 'large':
        case 'extra-large':
          return 64;
        default:
          return 48;
      }
    });
  }

  export function createIsMetricChartSelector(token: string) {
    return createSelector(
      createQueryTokenSelector(token),
      (state: ReportViewerState) => state.reportFeature.serializedReport.queryRunStatus,
      (queryToken, statuses) => statuses[queryToken]?.isMetric === true
    );
  }
}

export namespace EmbedTableSelectors {
  export function queryHasDisplayTable(queryToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.tables,
      (tables) => {
        for (const token in tables) {
          if (tables.hasOwnProperty(token)) {
            if (tables[token].queryToken) {
              return true;
            }
          }
        }

        return false;
      }
    );
  }

  export const hasAnyDisplayTable = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.tables,
    (tables) => Object.keys(tables).length > 0
  );

  export function createQueryTokenSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.tables[token],
      (table) => (table != null ? table.queryToken : null)
    );
  }

  export function createTableSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.tables[token],
      (table) => table
    );
  }

  export function createIsOrphanTableSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.tables,
      (tables) => tables[token] == null
    );
  }

  export function createTableViewSelector(token: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.tables[token],
      (table) => (table != null ? table.view : null)
    );
  }
}

export namespace FlamingoSelectors {
  export const getModel = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.views,
    (views) =>
      Object.keys(views).reduce(
        (model, queryToken) => Switch.Views.add(model, views[queryToken]),
        Switch.Models.empty()
      )
  );

  export function createRecordsSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.recordsByViz[vizToken],
      (data) => {
        if (data != null) {
          return data.content;
        }
        return undefined;
      }
    );
  }

  export function createDataProfilingSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.dataProfiling[vizToken],
      (data) => {
        return data || {};
      }
    );
  }

  export function createConditionalFormattingSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.conditionalFormatting[vizToken],
      (data) => {
        return data || {};
      }
    );
  }

  export function createModelSelector(chartToken: string) {
    return createSelector(
      EmbedChartSelectors.createQueryTokenSelector(chartToken),
      (state: ReportViewerState) =>
        state.reportFeature.serializedReport.flamingo.views[
          state.reportFeature.serializedReport.charts.queryTokens[chartToken]
        ],
      (queryToken, view) => Switch.Models.makeFromView(queryToken, view)
    );
  }

  const isValidSelectors = new Map<string, any>();
  export function createIsValidSelector(token: string) {
    const selector = createSelector(
      (state: ReportViewerState) =>
        state.reportFeature.serializedReport.flamingo.views[
          state.reportFeature.serializedReport.charts.queryTokens[token]
        ],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.encodings[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.queryTokens[token],
      (state: ReportViewerState) => state.reportFeature.serializedReport.charts.chartTypes[token],
      (view, encoding, queryToken, chartType) => {
        if (view && encoding && queryToken) {
          if (!EncodingHelpers.isChartEncodingComplete(encoding, chartType)) {
            return false;
          }

          const model = SwitchSchemaModel.make(Switch.Models.makeFromView(view.name, view), queryToken);
          if (chartType === 'table') {
            return EncodingHelpers.validateTableEncodingFields(encoding, model).length <= 0;
          } else {
            return EncodingHelpers.validateChartEncodingFields(encoding, model).length <= 0;
          }
        }

        if (view == null) {
          return undefined; // can't tell whether the chart is valid yet or not
        }

        return false;
      }
    );

    if (isValidSelectors.has(token)) {
      return isValidSelectors.get(token) as typeof selector;
    } else {
      isValidSelectors.set(token, selector);
      return selector;
    }
  }

  export function createColumnsSelector(token: string) {
    return (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.columnsByViz[token];
  }

  export function createPagingSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.recordsByViz[vizToken],
      (data) => {
        if (data != null) {
          const offset = data.offset || 0;
          const pageSize = data.pageSize || 100;
          return makePagingResult({ offset, pageSize }, data.count);
        }

        return undefined;
      }
    );
  }

  export function createPlanSuccessSelector(vizToken: string) {
    return (state: ReportViewerState) =>
      state.reportFeature.serializedReport.flamingo.statesByViz[vizToken] === FlamingoTypes.PlanStatus.Succeeded;
  }

  export function createPlanFailedSelector(vizToken: string) {
    return (state: ReportViewerState) =>
      state.reportFeature.serializedReport.flamingo.statesByViz[vizToken] === FlamingoTypes.PlanStatus.Failed;
  }

  export function createPlanLoadingSelector(vizToken: string) {
    return createSelector(
      createIsValidSelector(vizToken),
      (state: ReportViewerState) =>
        state.reportFeature.serializedReport.queryRunStatus[
          state.reportFeature.serializedReport.charts.queryTokens[vizToken]
        ],
      (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.statesByViz[vizToken],
      (isValid, runStatus, planStatus) => {
        if (runStatus != null) {
          if (runStatus.running) {
            // If the query is running, then let's assume that we're loading
            return true;
          } else {
            // incase the switch view hasn't been loaded yet
            if (runStatus.state === 'succeeded' && isValid === undefined) {
              return true;
            }

            // It's necessary to check this because there's a limbo state where
            // we won't have a plan for a chart until the schema is loaded
            if (isValid && planStatus == null) {
              return true;
            }

            // If planStatus is null then it must be invalid encoding
            return [FlamingoTypes.PlanStatus.Init, FlamingoTypes.PlanStatus.Loading].includes(planStatus);
          }
        }

        return false; // Assume nothing will ever run if there's no run status
      }
    );
  }

  export function createErrorSelector(vizToken: string) {
    return (state: ReportViewerState) => state.reportFeature.serializedReport.flamingo.errorsByViz[vizToken];
  }

  export function createFriendlyErrorSelector(vizToken: string) {
    return createSelector(createErrorSelector(vizToken), (response) => getFriendlyFlamingoError(response));
  }
}

export namespace EmbedNotebookSelectors {
  export function createVizSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.runState,
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.titles[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.resultTypes[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.contentTypes[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.contents[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.vizRunLinks[vizToken],
      (runState, title, resultType, contentType, content, vizLink) => ({
        token: vizToken,
        title,
        state: runState,
        results: zip(resultType, contentType, content).map(
          ([rt, ct, c]): NotebookEmbedVizResult => ({
            token: vizToken,
            result_type: rt,
            date: null,
            content_type: ct,
            type: ct,
            content: c,
          })
        ),
      })
    );
  }

  export function createVizStateSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.runState,
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.cellTokens[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.missingResults[vizToken],
      (runState, cellToken, missingResult) => {
        if (cellToken) {
          if (missingResult) {
            return NotebookVizState.Empty;
          }
          return NotebookVizState.Succeeded;
        } else if (cellToken === undefined && runState === NotebookRunState.Failed) {
          // [TODO][FIXME] Better fix would be to have this specific to cell Run in case of session timeout
          return NotebookVizState.Failed;
        } else if (runState === NotebookRunState.Skipped) {
          return NotebookVizState.Skipped;
        }
        return NotebookVizState.Loading;
      }
    );
  }

  export function createVizRenderSelector(vizToken: string) {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.titles[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.resultTypes[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.contentTypes[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.contents[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.vizLinks[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.vizRunLinks[vizToken],
      (state: ReportViewerState) => state.reportFeature.serializedReport.notebookVisuals.dates[vizToken],
      createVizStateSelector(vizToken),
      (title, resultType, contentType, content, vizLink, vizReportRunLink, date, vizState): NotebookVizRender => {
        if (title === undefined) {
          // assume if title is completely undefined, then this viz doesn't actually exist
          return null;
        }

        return {
          _name: 'pythonVisualization',
          id: 'python_' + vizToken,
          token: vizToken,
          frozen: false, // frozen only matters in the notebook view
          title,
          state: vizState,
          vizLink,
          vizReportRunLink,
          results: zip(resultType, contentType, content, date).map(
            ([rt, ct, c, dt]): NotebookEmbedVizResult => ({
              token: vizToken,
              result_type: rt,
              date: dt,
              content_type: ct,
              type: ct,
              content: c,
            })
          ),
        };
      }
    );
  }
}

export namespace EmbedReportSelectors {
  export const isThemeLoaded = (state: ReportViewerState) => state.reportFeature.themeLoaded;

  export const reportHeader = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.report.name,
    (state: ReportViewerState) => state.reportFeature.serializedReport.report.description,
    (name, description) => ({
      name,
      description,
    })
  );

  export const reportToken = (state: ReportViewerState) => state.reportFeature.serializedReport.report.token;

  export const chartVizTokens = (state: ReportViewerState) => state.reportFeature.serializedReport.charts.tokens;

  export const notebookVizTokens = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.notebookVisuals.vizTokens;

  export const tableVizTokens = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.tables,
    (tableMap) => Object.keys(tableMap)
  );

  export const fullWidth = (state: ReportViewerState) => state.reportFeature.serializedReport.report.fullWidth;

  export const isDrillAnywhereEnabled = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.report.drillAnywhereEnabled;

  export const reportRunCreatedAt = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.ui.captureReportRunCreatedAt;

  export const reportFilterControls = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.filters.controls,
    (filters) => filters.map((f) => ReportFilterControlHelpers.toControl(f))
  );

  export const reportFilterControlRecords = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.filters.controls;

  export const reportFilterOptions = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.filters.options,
    (options) => options
  );

  export const reportFilterOptionsCount = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.filters.totalOptionCount,
    (options) => options
  );

  export const reportFilterOptionsSelectedCount = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.filters.selectedOptionCount,
    (options) => options
  );

  export const isFilterPanelOpen = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.ui.filterPanelOpen;

  export const reportLayout = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.report && state.reportFeature.serializedReport.report.layout;

  export const account = (state: ReportViewerState) => state.reportFeature.serializedReport.account;

  export const loggedIn = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.account && state.reportFeature.serializedReport.account.loggedIn;
  export const reportTheme = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.report && state.reportFeature.serializedReport.report.theme;

  export const viewerType = createSelector(
    (state: ReportViewerState) =>
      state.reportFeature.serializedReport.account && state.reportFeature.serializedReport.account.loggedIn,
    (state: ReportViewerState) =>
      state.reportFeature.serializedReport.account && state.reportFeature.serializedReport.account.whiteLabel,
    (loggedIn, whiteLabel) => {
      if (whiteLabel) {
        return 'whiteLabel';
      }
      if (!loggedIn) {
        return 'guest';
      }
    }
  );

  // This is within the report context
  export const embedded = (state: ReportViewerState) =>
    state.reportFeature.serializedReport.account && state.reportFeature.serializedReport.account.embedded;

  export const features = createSelector(
    (state: ReportViewerState) => state.reportFeature.serializedReport.features,
    (features) => new Set(features.map((f) => f.context + '.' + f.type))
  );

  export const drilldownEnabled = createSelector(features, (features) =>
    FEATURE_FLAG_MAP.get(Features.Report.DrillDown).every((flag) => features.has(flag.context + '.' + flag.type))
  );

  export const exploreEnabled = createSelector(features, (features) =>
    FEATURE_FLAG_MAP.get(Features.Report.Explore).every((flag) => features.has(flag.context + '.' + flag.type))
  );

  export const getOrphanedFilters = createSelector(FlamingoSelectors.getModel, reportFilterControls, (model, filters) =>
    filters.filter((filter) => Flamingo.modelHasSomeMatchingField(model, filter) === false)
  );

  export function createDatasetSelector(queryToken: string) {
    return (state: ReportViewerState) => state.reportFeature.serializedReport.legacyEmbedDatasets[queryToken];
  }

  export function createAlamodeConfigSelector(
    vizToken: string
  ): Selector<ReportViewerState, ChartTypes.FlatTableCustomConfig> {
    return createSelector(
      (state: ReportViewerState) => state.reportFeature.alamodeCustomSettings[vizToken],
      (alamodeCustomSettings) => alamodeCustomSettings
    );
  }
}
